/* eslint-disable @nx/enforce-module-boundaries */
import { useDisclosure, useOutsideClick } from '@mybridge/ui/hooks';
import { Input, InputGroup, InputLeftElement } from '@mybridge/ui/input';
import { List, ListItem } from '@mybridge/ui/list';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@mybridge/ui/popover';
import { useEffect, useRef, useState } from 'react';
import { useCountries } from 'v4/lib/hooks/use-countries';

export const CountryDropdown = ({
  value,
  defaultValue,
  onChange,
  leftIcon,
  ...props
}) => {
  const inputRef = useRef();
  const containerRef = useRef();
  const { countries } = useCountries({});
  const disc = useDisclosure();
  const [selected, setSelected] = useState(defaultValue);
  const [search, setSearch] = useState(defaultValue?.name || value?.country);
  const popoverOpenRef = useRef(false);

  // useEffect(() => {
  //   if (value && countries?.length) {
  //     const sel = countries.find(
  //       (c) =>
  //         c?.name === value?.name ||
  //         c?.name === value?.country ||
  //         c?.code === value?.code
  //     );
      
  //     // Update only if the selection has changed
  //     if (sel && selected?.code !== sel?.code) {
  //       setSelected(sel);
  //       setSearch(sel?.name); // Update the search when `value` changes, not when typing
  //     }
  //   }
  // }, [value, countries]);

  useEffect(() => {
    if (value && countries?.length) {
      const sel = countries.find(
        (c) =>
          c?.name === value?.name ||
          c?.name === value?.country ||
          c?.code === value?.code
      );
      
      // Update whenever value changes, not just when selection changes
      if (sel) {
        setSelected(sel);
        setSearch(sel?.name); // Always update search when value changes
      }
    }
  }, [value, countries]);

  const handleSelect = (item) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Set a small delay to ensure the click/touch event completes
    setTimeout(() => {
      setSelected(item);
      onChange?.(item);
      setSearch(item?.name);
      disc?.onClose?.();
    }, 10);
  };

  // Add touch event handlers for better touch device support
  const handleTouchStart = (item) => (e) => {
    // Prevent default to avoid any browser-specific touch behaviors
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTouchEnd = (item) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Handle selection on touch end
    setSelected(item);
    onChange?.(item);
    setSearch(item?.name);
    disc?.onClose?.();
  };

  // Track popover state
  useEffect(() => {
    popoverOpenRef.current = disc.isOpen;
  }, [disc.isOpen]);

  return (
    <Popover
      {...disc}
      matchWidth={true}
      initialFocusRef={inputRef}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <InputGroup>
          {leftIcon ? <InputLeftElement>{leftIcon}</InputLeftElement> : ''}
          <Input
            pl={leftIcon ? 10 : 4}
            ref={inputRef}
            value={search}
            sx={{ textTransform: 'capitalize' }}
            onKeyUp={(e) =>
              e?.keyCode === 27 ? disc?.onClose?.() : disc?.onOpen?.()
            }
            onChange={(e) => {
              setSearch(e.target.value);
              disc?.onOpen?.();
            }}
            onFocus={() => {
              disc?.onOpen?.();
            }}
            onBlur={(e) => {
              // Use a small delay to allow click events to complete
              setTimeout(() => {
                // Only close if we're not interacting with the list
                if (popoverOpenRef.current && !containerRef.current?.contains(document.activeElement)) {
                  disc?.onClose?.();
                }
              }, 150);
            }}
            placeholder="Type to search..."
            {...props}
          />
        </InputGroup>
      </PopoverTrigger>
      {countries?.length && search?.length > 0 ? (
        <PopoverContent
          ref={containerRef}
          p={0}
          w="100%"
          maxH="200px"
          overflowY="auto"
        >
          <PopoverBody p={0}>
            <List>
              {countries
                ?.filter?.((c) =>
                  c?.name?.toLowerCase?.()?.includes(search?.toLowerCase?.())
                )
                ?.map?.((c, cind) => (
                  <ListItem
                    key={cind}
                    _hover={{
                      bg: 'gray.100',
                    }}
                    px={4}
                    py={2}
                    cursor="pointer"
                    onClick={handleSelect(c)}
                    onTouchStart={handleTouchStart(c)}
                    onTouchEnd={handleTouchEnd(c)}
                    role="button"
                    tabIndex={0}
                    // Add keyboard support
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSelect(c)(e);
                      }
                    }}
                  >
                    {c.name}
                  </ListItem>
                ))}
            </List>
          </PopoverBody>
        </PopoverContent>
      ) : (
        ''
      )}
    </Popover>
  );
};
